import type { Building } from '../types/Building';

interface WalkingRoute {
  name: string;
  description: string;
  buildings: Building[];
  estimatedTime: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  distance: string;
}

export function WalkingRoutes({ buildings }: { buildings: Building[] }) {
  const routes: WalkingRoute[] = [
    {
      name: "Financial District Supertalls",
      description: "Lower Manhattan's true supertalls including One World Trade Center",
      buildings: buildings.filter(b => 
        ['One World Trade Center', '3 World Trade Center'].includes(b.name)
      ),
      estimatedTime: "1-2 hours",
      difficulty: "Easy",
      distance: "0.5 miles"
    },
    {
      name: "Midtown Classics",
      description: "Iconic supertalls including the Empire State and Chrysler Buildings",
      buildings: buildings.filter(b => 
        ['Empire State Building', 'Chrysler Building', 'Bank of America Tower', 'The New York Times Building', 'One Vanderbilt'].includes(b.name)
      ),
      estimatedTime: "3-4 hours", 
      difficulty: "Medium",
      distance: "2.2 miles"
    },
    {
      name: "Billionaires' Row",
      description: "Ultra-luxury residential supertalls on and around 57th Street (includes under construction)",
      buildings: buildings.filter(b => 
        ['Central Park Tower', '111 West 57th Street', '432 Park Avenue', 'One57', '53W53', '41–47 West 57th Street'].includes(b.name)
      ),
      estimatedTime: "2-3 hours",
      difficulty: "Easy", 
      distance: "1.0 miles"
    },
    {
      name: "Hudson Yards Adventure",
      description: "The newest supertall development in Manhattan",
      buildings: buildings.filter(b => 
        ['30 Hudson Yards', '35 Hudson Yards', 'The Spiral', 'One Manhattan West'].includes(b.name)
      ),
      estimatedTime: "2 hours",
      difficulty: "Easy",
      distance: "0.7 miles"
    },
    {
      name: "Under Construction Special",
      description: "Visit the 2 supertalls currently being built - witness NYC's future skyline in progress",
      buildings: buildings.filter(b => b.status === 'Under construction'),
      estimatedTime: "1-2 hours",
      difficulty: "Easy", 
      distance: "1.5 miles"
    },
    {
      name: "Complete Supertalls Tour",
      description: "All 21 supertalls (19 completed + 2 under construction) - epic full-day adventure",
      buildings: buildings,
      estimatedTime: "6-8 hours",
      difficulty: "Hard", 
      distance: "5+ miles"
    }
  ];

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-700 bg-green-100';
      case 'Medium': return 'text-yellow-700 bg-yellow-100';
      case 'Hard': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">🗺️ Suggested Walking Routes</h2>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {routes.map((route, index) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-bold text-lg text-gray-900">{route.name}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(route.difficulty)}`}>
                {route.difficulty}
              </span>
            </div>
            
            <p className="text-gray-600 text-sm mb-3">{route.description}</p>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Buildings:</span>
                <span className="font-medium">{route.buildings.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Distance:</span>
                <span className="font-medium">{route.distance}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Time:</span>
                <span className="font-medium">{route.estimatedTime}</span>
              </div>
            </div>
            
            <div className="border-t pt-3">
              <p className="text-xs text-gray-500 mb-2">Buildings on this route:</p>
              <div className="flex flex-wrap gap-1">
                {route.buildings.slice(0, 3).map((building) => (
                  <span 
                    key={building.rank} 
                    className={`inline-block text-xs px-2 py-1 rounded ${
                      building.status === 'Under construction' 
                        ? 'bg-orange-100 text-orange-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {building.status === 'Under construction' && '🚧 '}
                    #{building.rank} {building.name.length > 15 ? building.name.substring(0, 15) + '...' : building.name}
                  </span>
                ))}
                {route.buildings.length > 3 && (
                  <span className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">
                    +{route.buildings.length - 3} more
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">📱 Pro Tips for Your Walking Tour:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Download offline maps in case of poor cell service</li>
          <li>• Wear comfortable walking shoes</li>
          <li>• Bring water and snacks for longer routes</li>
          <li>• Check building observation decks for inside views</li>
          <li>• Best photography light is 1-2 hours before sunset</li>
          <li>• Some buildings offer free lobby viewing areas</li>
          <li>• 🚧 Under construction buildings: observe from street level for safety</li>
        </ul>
      </div>
    </div>
  );
}